import {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ActionStatus,
  ActionType,
  IUpcomingActionsSummaryRow,
  UpcomingActionsHorizon,
  langauge,
} from "shared";
import { When } from "react-if";
import { useNavigate } from "react-router";

import { Card } from "../../../../common/Atoms/Card";
import { Button } from "../../../../common/Atoms/Button";
import { MultiButtonCardHeader } from "../../../../common/Atoms/MultiButtonCardHeader";
import { useActionTableModels, useUpcomingActionsSummary } from "../../../hooks/useActions";
import { LoadingOverlay } from "../../Molecules/LoadingOverlay";
import { Paragraph } from "../../../../common/Atoms/Typography/Paragraph";
import { useGetSelectedOrganisation } from "../../../hooks/useGetSelectedOrganisation";
import { classNames } from "../../../../common/lib/classNames";

/** Standard display brackets, each merging one or more backend horizons (chronological order) */
const displayGroups: { label: string; keys: UpcomingActionsHorizon[] }[] = [
  { label: `Next 24 hours`, keys: [`h24`] },
  { label: `2–7 days`, keys: [`d2_7`] },
  { label: `8–30 days`, keys: [ `d8_14`, `d15_21`, `d22_30` ] },
  { label: `Later`, keys: [ `d31_60`, `d61_90`, `later` ] },
];

/** Labels when a single busy bracket is split into its finer horizons */
const splitLabels: Partial<Record<UpcomingActionsHorizon, string>> = {
  d8_14: `8–14 days`,
  d15_21: `15–21 days`,
  d22_30: `22–30 days`,
  d31_60: `31–60 days`,
  d61_90: `61–90 days`,
  later: `90+ days`,
};

type BracketEntry = { type: ActionType; count: number; names: string[] };

/** Merge rows across the given horizons into one entry per action type */
function mergeRows(rows: IUpcomingActionsSummaryRow[], keys: UpcomingActionsHorizon[]): BracketEntry[] {
  const byType = new Map<ActionType, BracketEntry>();

  for (const key of keys) {
    for (const row of rows.filter(r => r.horizon === key)) {
      const entry = byType.get(row.type) || { type: row.type, count: 0, names: [] };

      entry.count += row.count;
      entry.names.push(...row.sampleNames);
      byType.set(row.type, entry);
    }
  }

  return [...byType.values()].sort((a, b) => b.count - a.count);
}

const orangeTypes = [
  ActionType.ESCALATION,
  ActionType.LATE_FEE,
  ActionType.LATE_FEE_EMAIL,
  ActionType.ISSUE_STATEMENT_LATE_FEE,
];

const greenTypes = [
  ActionType.REMINDER,
];

const purpleTypes = [
  ActionType.SEND_STATEMENT,
];

function badgeClasses(type: ActionType) {
  if (orangeTypes.includes(type)) return `text-orange-800 bg-orange-50 border-orange-200`;
  if (greenTypes.includes(type)) return `text-green-800 bg-green-50 border-green-200`;
  if (purpleTypes.includes(type)) return `text-violet-800 bg-violet-50 border-violet-200`;

  return `text-paidniceGreen-600 bg-paidniceGreen-50 border-paidniceGreen-100`;
}

function formatNames(entry: BracketEntry) {
  const names = [...new Set(entry.names)].slice(0, 2);
  const more = entry.count - names.length;

  return { names: names.join(`, `), more };
}

export function ActionableTasks() {
  const currentOrg = useGetSelectedOrganisation();
  const { setCurrentTab, setFilterModel, setSortModel } = useActionTableModels();
  const navigate = useNavigate();
  const [ mode, setMode ] = useState<`drafts` | `scheduled`>(`drafts`);

  const { data: summaryRes, isLoading } = useUpcomingActionsSummary();

  const status = mode === `drafts` ? ActionStatus.APPROVAL_PENDING : ActionStatus.PENDING;
  const noun = mode === `drafts` ? `draft` : `action`;
  const verb = mode === `drafts` ? `Review` : `View`;

  const { rows, totals } = useMemo(() => {
    const allRows = summaryRes?.rows || [];

    return {
      rows: allRows.filter(row => row.status === status),
      totals: {
        drafts: allRows.filter(row => row.status === ActionStatus.APPROVAL_PENDING).reduce((sum, row) => sum + row.count, 0),
        scheduled: allRows.filter(row => row.status === ActionStatus.PENDING).reduce((sum, row) => sum + row.count, 0),
      },
    };
  }, [summaryRes, status]);

  const total = mode === `drafts` ? totals.drafts : totals.scheduled;

  // On first load only: no drafts to review, default to scheduled view
  useEffect(() => {
    if (!isLoading && totals.drafts === 0) {
      setMode(`scheduled`);
    }
  }, [isLoading]);

  const brackets = useMemo(() => {
    const groups = displayGroups
      .map(group => ({ label: group.label, keys: group.keys, entries: mergeRows(rows, group.keys) }))
      .filter(group => group.entries.length);

    // Everything in one bracket: split it into its finer horizons to spread the timeline out
    if (groups.length === 1 && groups[0].keys.length > 1) {
      const split = groups[0].keys
        .map(key => ({ label: splitLabels[key] || groups[0].label, keys: [key], entries: mergeRows(rows, [key]) }))
        .filter(group => group.entries.length);

      if (split.length > 1) return split;
    }

    return groups;
  }, [rows]);

  function goToActions(type?: ActionType) {
    setCurrentTab(status);

    setFilterModel({
      items: type ? [
        {
          field: `type`,
          operator: `is`,
          value: type,
          id: Math.round(Math.random() * 1e5),
        },
      ] : [],
    });

    setSortModel([
      {
        field: `actionOn`,
        sort: `asc`,
      },
    ]);

    navigate(`/${currentOrg?.id}/actions?persist=true`);
  }

  return (
    <LoadingOverlay loading={ isLoading }
      className={ `h-full` }>
      <Card className={ `w-full h-full flex flex-col` }
        variant={ `soft` }>
        <MultiButtonCardHeader
          title={ `Upcoming Actions` }
          description={
            mode === `drafts` ?
              `Drafts send only after you approve them` :
              `Scheduled actions send automatically — no approval needed`
          }
          noDivider
          variant={ `plain` }
          className={ `mb-4` }
          cta={
            <div className={ `flex shrink-0 gap-0.5 rounded-lg bg-slate-100 p-[3px]` }>
              {
                ([
                  [ `drafts`, `Drafts`, totals.drafts ],
                  [ `scheduled`, `Scheduled`, totals.scheduled ],
                ] as const).map(([ key, label, count ]) => (
                  <button
                    key={ key }
                    type={ `button` }
                    onClick={ () => setMode(key) }
                    className={
                      classNames(
                        `rounded-md px-3 py-1 text-sm font-medium cursor-pointer`,
                        mode === key ?
                          `bg-white font-semibold text-paidniceGreen-800 shadow-sm` :
                          `text-gray-500 hover:text-paidniceGreen-800`,
                      )
                    }
                  >
                    { label }
                    { ` ` }
                    <span className={ mode === key ? `font-medium text-gray-500` : `text-gray-400` }>
                      { count }
                    </span>
                  </button>
                ))
              }
            </div>
          }
        />

        <When condition={ !isLoading && total === 0 }>
          <div className={ `flex grow justify-center items-center min-h-[100px]` }>
            <Paragraph>
              {
                mode === `drafts` ?
                  `No upcoming draft actions to review` :
                  `No upcoming scheduled actions`
              }
            </Paragraph>
          </div>
        </When>

        <When condition={ total > 0 }>
          { /* Timeline of horizons */ }
          <div className={ `flex grow flex-col` }>
            {
              brackets.map((bracket, index) => {
                const bracketTotal = bracket.entries.reduce((sum, entry) => sum + entry.count, 0);
                const isLast = index === brackets.length - 1;

                return (
                  <div key={ bracket.label }
                    className={ `flex gap-4` }>
                    { /* Dot + connector */ }
                    <div className={ `flex w-3 flex-col items-center` }>
                      <span className={
                        classNames(
                          `mt-[5px] h-2.5 w-2.5 rounded-full`,
                          index === 0 && `bg-paidniceGreen-600`,
                          index > 0 && !isLast && `bg-paidniceGreen-400`,
                          index > 0 && isLast && `border-2 border-paidniceGreen-200 bg-white`,
                        )
                      } />
                      <When condition={ !isLast }>
                        <span className={ `w-0.5 flex-1 bg-paidniceGreen-100` } />
                      </When>
                    </div>
                    <div className={ classNames(`flex-1 min-w-0`, !isLast && `pb-10`) }>
                      <div className={ `mb-3 flex items-baseline gap-2` }>
                        <span className={
                          classNames(
                            `text-xs font-bold uppercase tracking-wider`,
                            index === 0 ? `text-paidniceGreen-600` : `text-gray-500`,
                          )
                        }>
                          { bracket.label }
                        </span>
                        <Paragraph as={ `span` }
                          variant={ `help` }
                          size={ `xs` }>
                          { `${bracketTotal} ${noun}${bracketTotal === 1 ? `` : `s`}` }
                        </Paragraph>
                      </div>
                      <div className={ `flex flex-col gap-2.5` }>
                        {
                          bracket.entries.map(entry => {
                            const { names, more } = formatNames(entry);

                            return (
                              <div key={ entry.type }
                                className={ `flex items-center gap-3` }>
                                <span className={
                                  classNames(
                                    `whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium`,
                                    badgeClasses(entry.type),
                                  )
                                }>
                                  { langauge.action[entry.type]?.labelShort || langauge.action[entry.type]?.label || entry.type }
                                </span>
                                <Paragraph className={ `min-w-0 flex-1 truncate` }
                                  variant={ `secondary` }>
                                  { names }
                                  <When condition={ more > 0 }>
                                    <Paragraph as={ `span` }
                                      variant={ `help` }>
                                      { ` +${more} more` }
                                    </Paragraph>
                                  </When>
                                </Paragraph>
                                <a
                                  href={ `#` }
                                  onClick={ e => {
                                    e.preventDefault();
                                    goToActions(entry.type);
                                  } }
                                >
                                  <Paragraph variant={ `link` }
                                    className={ `whitespace-nowrap` }>
                                    { `${verb} ${entry.count}` }
                                  </Paragraph>
                                </a>
                              </div>
                            );
                          })
                        }
                      </div>
                    </div>
                  </div>
                );
              })
            }
          </div>

          <div className={ `mt-auto flex justify-center pt-6` }>
            <Button
              color={ `paidniceGreenOutline` }
              size={ `sm` }
              className={ `font-light` }
              onClick={ () => goToActions() }
            >
              { `${verb} all ${total} ${mode === `drafts` ? `draft` : `scheduled`} action${total === 1 ? `` : `s`}` }
            </Button>
          </div>
        </When>
      </Card>
    </LoadingOverlay>
  );
}
